import React, { useEffect, useState } from 'react';
import styles from '../styles/styles.module.css';
import { useDispatch, useSelector } from 'react-redux';
import { FaRegTrashAlt, FaPen } from 'react-icons/fa';
import { deleteSite } from '../../../api/site/post';
import { ClockLoader } from 'react-spinners';
import { removeSite } from '../../../Redux/slices/allSites';
// IMPORT ALL SETTERS FOR THE FORM
import {
  setSiteName,
  setCcPhone,
  setSiteStreet,
  setSiteCity,
  setSiteState,
  setSiteZip,
  setTimezone,
  setShiftStartHours,
  setShiftStartMinutes,
  setSiteId, // You need to ensure this exists in your slice
} from '../../../Redux/slices/siteSlice';
import ModalSmall from '../../../components/ModalSmall/ModalSmall';
import { redirect } from 'react-router-dom';

// Accept the prop to switch views
interface SitesInterface {
  setAddSiteActive: React.Dispatch<React.SetStateAction<boolean>>;
}

export const Sites: React.FC<SitesInterface> = ({ setAddSiteActive }) => {
  const { sites } = useSelector((state: any) => state.associatedSites);
  const { userType, companyPermissions, sitePermissions } = useSelector(
    (state: any) => state.user
  );
  const dispatch = useDispatch();

  // ... (Permission logic remains the same) ...
  function canView() {
    if (userType === 'HYPER_ADMIN' || userType === 'COMPANY_ADMIN') {
      return { canEdit: true, canDelete: true };
    } else if (sitePermissions.editSites) {
      return { canEdit: true, canDelete: false };
    } else {
      return { canEdit: false, canDelete: false };
    }
  }

  const [currentlyDeleting, setCurrentlyDeleting] = useState<Array<any>>([]);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [deleteId, setDeleteId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (userType !== 'HYPER_ADMIN' || userType !== 'COMPANY_ADMIN') {
      redirect('/');
    }
  }, [userType]);

  // ... (Delete logic remains the same) ...
  function stageForDelete(id: string, index: number) {
    setDeleteId(id);
    setDeleteIndex(index);
    setCurrentlyDeleting((prevVal) => [...currentlyDeleting, index]);
  }

  function handleCancel() {
    setDeleteId('');
    setDeleteIndex(null);
    setCurrentlyDeleting((prevVal) =>
      prevVal.filter((ind: any) => ind !== deleteIndex)
    );
  }

  async function handleDelete() {
    setIsLoading(true);
    try {
      const res = await deleteSite(deleteId);
      setCurrentlyDeleting((prevVal) =>
        prevVal.filter((ind: any) => ind !== deleteIndex)
      );
      if (res?.success) {
        setDeleteId('');
        setDeleteIndex(null);
        dispatch(removeSite(res.data._id));
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }

  // NEW: Handler to populate Redux and switch view
  function handleEdit(site: any) {
    dispatch(setSiteId(site._id)); // Important: Track ID to know we are editing
    dispatch(setSiteName(site.siteName || ''));
    dispatch(setCcPhone(site.sitePhone || '')); // Mapping sitePhone to ccPhone
    dispatch(setSiteStreet(site.siteStreet || ''));
    dispatch(setSiteCity(site.siteCity || ''));
    dispatch(setSiteState(site.siteState || ''));
    dispatch(setSiteZip(site.siteZip || ''));
    dispatch(setTimezone(site.timezone || ''));
    console.log(site);
    // Handle Time Parsing (Assuming HH:MM format from backend)
    if (site.shiftStartTime) {
      const [h, m] = site.shiftStartTime.split(':');
      dispatch(setShiftStartHours(h));
      dispatch(setShiftStartMinutes(m));
    }

    setAddSiteActive(true); // Open the form
  }

  return (
    <div className={styles.sitesWrapper}>
      <table className={styles.addSiteTable}>
        {/* ... (Thead remains the same) ... */}
        <thead className={styles.tableHeadAll}>
          <tr>
            <th className={styles.tableHead}>Site Name</th>
            <th className={styles.tableHead}>CC Phone</th>
            <th className={styles.tableHead}>Detail Lead</th>
            <th className={styles.tableHead}>DL Phone</th>
            <th className={styles.tableHead}>Address</th>
            {canView().canDelete || canView().canEdit ? (
              <th className={styles.tableHead}>Action</th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {sites.map((site: any, index: number) => {
            return (
              <tr key={site._id} className={styles.tableBodyRow}>
                <td className={styles.tableBody}>{site.siteName}</td>
                <td className={styles.tableBody}>{site.sitePhone}</td>
                <td className={styles.tableBody}>{site.siteLead}</td>
                <td className={styles.tableBody}>{site.siteLeadPhone}</td>
                <td className={styles.tableBody}>
                  {`${site.siteStreet ? site.siteStreet : ''}${site.siteStreet && site.siteCity ? ', ' : ''}${site.siteCity ? site.siteCity : ''}${(site.siteCity || site.siteStreet) && site.siteState ? ', ' : ''}${site.siteState ? site.siteState : ''}${site.siteZip ? ' ' + site.siteZip : ''}`}
                </td>

                {/* ACTIONS */}
                {(canView().canEdit || canView().canDelete) && (
                  <td className={styles.tableBody}>
                    <div
                      style={{
                        display: 'flex',
                        gap: '10px',
                        justifyContent: 'center',
                      }}
                    >
                      {/* EDIT BUTTON */}
                      {canView().canEdit && (
                        <div
                          onClick={() => handleEdit(site)}
                          style={{ cursor: 'pointer', color: '#666' }}
                        >
                          <FaPen />
                        </div>
                      )}

                      {/* DELETE BUTTON */}
                      {canView().canDelete && (
                        <div
                          onClick={() => stageForDelete(site._id, index)}
                          style={{ cursor: 'pointer', color: '#666' }}
                        >
                          {currentlyDeleting.includes(index) ? (
                            <ClockLoader size={18} />
                          ) : (
                            companyPermissions.deleteSites && <FaRegTrashAlt />
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      {currentlyDeleting.length > 0 ? (
        <ModalSmall
          submitButtonText="Delete Anyway"
          title="Warning"
          text="Deleting this site will make it unavailable to your protectors."
          loading={isLoading}
          submitHandler={handleDelete}
          cancelHandler={handleCancel}
        />
      ) : null}
    </div>
  );
};

export default Sites;
